import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { endorsementCatalog } from '@/shared/api/resources';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { statusLabel } from '@/shared/ui/statusLabels';

import { tiposDaCotacao } from '../cotacao';
import {
  avisoDoDocumento,
  avisoDoPeriodo,
  filtrosDaCorretoraVazios,
  situacoesDaCorretora,
  tiposDeCosseguro,
  type BrokerQuoteFilters,
} from './cotacaoDaCorretora';

/**
 * Dialogo "Cotacoes" da corretora, com os filtros na ordem do OnPoint (NCT-026 a
 * NCT-037).
 *
 * Os filtros so valem ao aplicar. "Tipo de cosseguro" filtra pelo tipo gravado no
 * financeiro da cotacao (P-COT-4), com os valores do SeguraCore: as opcoes do OnPoint nao
 * foram lidas (A CONFIRMAR). O servidor mantem o escopo da corretora.
 */
export function BrokerQuoteFiltersDialog({
  open,
  value,
  onApply,
  onClose,
}: {
  open: boolean;
  value: BrokerQuoteFilters;
  onApply: (filtros: BrokerQuoteFilters) => void;
  onClose: () => void;
}) {
  const [rascunho, setRascunho] = useState<BrokerQuoteFilters>(value);
  const [aberto, setAberto] = useState(open);
  if (open !== aberto) {
    setAberto(open);
    if (open) setRascunho(value);
  }

  function campo<K extends keyof BrokerQuoteFilters>(chave: K, valor: string) {
    setRascunho((atual) => ({ ...atual, [chave]: valor }));
  }

  const tiposDeEndosso = useQuery({
    queryKey: ['endorsement-types', 'todos'],
    queryFn: ({ signal }) => endorsementCatalog.types(false, signal),
    enabled: open,
    retry: false,
  });

  const avisoPeriodo = avisoDoPeriodo(rascunho);
  const avisoTomador = avisoDoDocumento(rascunho.policyholder_document);
  const avisoSegurado = avisoDoDocumento(rascunho.insured_document);
  const invalido = Boolean(avisoPeriodo || avisoTomador || avisoSegurado);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cotacoes"
      description="Filtros da lista de cotacoes da corretora."
      size="md"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => setRascunho(filtrosDaCorretoraVazios)}
          >
            Limpar filtro
          </Button>
          <Button disabled={invalido} onClick={() => onApply(rascunho)}>
            Aplicar filtro
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Nro da Cotacao"
          placeholder="Digite"
          inputMode="numeric"
          value={rascunho.number}
          onChange={(event) => campo('number', event.target.value)}
        />
        <Select
          label="Status da apolice"
          placeholder="Selecione"
          value={rascunho.proposal_status}
          options={situacoesDaCorretora.map((situacao) => ({
            value: situacao,
            label: statusLabel('cotacao', situacao),
          }))}
          onChange={(event) => campo('proposal_status', event.target.value)}
        />
        <Field
          label="Nome do Tomador"
          placeholder="Digite"
          value={rascunho.policyholder_name}
          onChange={(event) => campo('policyholder_name', event.target.value)}
        />
        <Field
          label="Documento do tomador"
          placeholder="Digite"
          value={rascunho.policyholder_document}
          error={avisoTomador}
          onChange={(event) => campo('policyholder_document', event.target.value)}
        />
        <Field
          label="Nome do Segurado"
          placeholder="Digite"
          value={rascunho.insured_name}
          onChange={(event) => campo('insured_name', event.target.value)}
        />
        <Field
          label="CNPJ Segurado"
          placeholder="Digite"
          value={rascunho.insured_document}
          error={avisoSegurado}
          onChange={(event) => campo('insured_document', event.target.value)}
        />
        <Field
          label="Data inicial"
          type="date"
          value={rascunho.created_from}
          onChange={(event) => campo('created_from', event.target.value)}
        />
        <Field
          label="Data final"
          type="date"
          value={rascunho.created_to}
          error={avisoPeriodo}
          onChange={(event) => campo('created_to', event.target.value)}
        />
        <Select
          label="Tipo da cotacao"
          placeholder="Selecione"
          value={rascunho.proposal_type}
          options={tiposDaCotacao.map((tipo) => ({
            value: tipo,
            label: statusLabel('tipoCotacao', tipo),
          }))}
          onChange={(event) => campo('proposal_type', event.target.value)}
        />
        <Select
          label="Tipo de endosso"
          placeholder="Selecione"
          value={rascunho.endorsement_type_id}
          disabled={!tiposDeEndosso.isSuccess}
          options={(tiposDeEndosso.data ?? []).map((tipo) => ({
            value: tipo.id,
            label: tipo.name,
          }))}
          onChange={(event) => campo('endorsement_type_id', event.target.value)}
        />
        <Select
          label="Tipo de cosseguro"
          placeholder="Selecione"
          value={rascunho.coinsurance_type}
          options={tiposDeCosseguro}
          onChange={(event) => campo('coinsurance_type', event.target.value)}
        />
      </div>
    </Modal>
  );
}
